// @flow
import * as React from 'react';
import * as Immutable from 'immutable';
import { AllocatedTasks } from 'common/models/internal/PlanAllocated';
import { UnallocatedTasks } from 'common/models/internal/PlanUnallocated';

export type VisitIdentifierType = {
    sourceId: string,
    id: string,
    transactionType: ?string
};

export type TourVisitIdentifierType = {
    tourId: string,
    visitId: VisitIdentifierType
};

export type TourVisitIndexIdentifierType = {
    tourIndex: number,
    visitId: VisitIdentifierType
};

export type VisitStatesType = {
    hoveredVisit: ?TourVisitIdentifierType,
    selectedVisits: Immutable.List<TourVisitIdentifierType>,
    activeVisit: ?TourVisitIdentifierType,
    hoveredVisitIndex: ?TourVisitIndexIdentifierType,
    activeVisitIndex: ?TourVisitIndexIdentifierType,
    searchedTask: ?AllocatedTasks | UnallocatedTasks
};
const visitStates: VisitStatesType = {
    hoveredVisit: null,
    selectedVisits: Immutable.List(),
    activeVisit: null,
    hoveredVisitIndex: null,
    activeVisitIndex: null,
    searchedTask: null
};

export const VisitStatesActionTypes = Object.freeze({
    SET_HOVERED_VISIT_INDEX: 'SET_HOVERED_VISIT_INDEX',
    SET_HOVERED_VISIT: 'SET_HOVERED_VISIT',
    SET_ACTIVE_VISIT_INDEX: 'SET_ACTIVE_VISIT_INDEX',
    SET_ACTIVE_VISIT: 'SET_ACTIVE_VISIT',
    SET_SELECTED_VISITS: 'SET_SELECTED_VISITS',
    SET_SEARCHED_TASK: 'SET_SEARCHED_TASK'
});

type ActionType =
    | {
          type: 'SET_HOVERED_VISIT_INDEX',
          data: ?TourVisitIndexIdentifierType
      }
    | {
          type: 'SET_HOVERED_VISIT',
          data: ?TourVisitIdentifierType
      }
    | {
          type: 'SET_ACTIVE_VISIT_INDEX',
          data: ?TourVisitIndexIdentifierType
      }
    | {
          type: 'SET_ACTIVE_VISIT',
          data: ?TourVisitIdentifierType
      }
    | {
          type: 'SET_SELECTED_VISITS',
          data: Immutable.List<TourVisitIdentifierType>
      }
    | {
          type: 'SET_SEARCHED_TASK',
          data: ?AllocatedTasks | UnallocatedTasks
      };

type Dispatch<A> = A => void;

export type VisitStatesDispatch = Dispatch<ActionType>;

export const VisitStatesContext = React.createContext<
    [VisitStatesType, Dispatch<ActionType>]
>([visitStates, () => {}]);

const reducer = (state: VisitStatesType, action: ActionType) => {
    switch (action.type) {
        case VisitStatesActionTypes.SET_HOVERED_VISIT_INDEX:
            return {
                ...state,
                hoveredVisitIndex: action.data
            };

        case VisitStatesActionTypes.SET_HOVERED_VISIT:
            return {
                ...state,
                hoveredVisit: action.data
            };

        case VisitStatesActionTypes.SET_ACTIVE_VISIT_INDEX:
            return {
                ...state,
                activeVisitIndex: action.data
            };

        case VisitStatesActionTypes.SET_ACTIVE_VISIT:
            return {
                ...state,
                activeVisit: action.data
            };

        case VisitStatesActionTypes.SET_SELECTED_VISITS:
            return {
                ...state,
                selectedVisits: action.data
            };

        case VisitStatesActionTypes.SET_SEARCHED_TASK: {
            const task = action.data;
            return {
                ...state,
                searchedTask: task
            };
        }
        default:
            return state;
    }
};

export const VisitStateProvider = ({ children }) => {
    const initialState = React.useReducer<VisitStatesType, ActionType>(
        reducer,
        visitStates
    );
    return (
        <VisitStatesContext.Provider value={initialState}>
            {children}
        </VisitStatesContext.Provider>
    );
};

export const withVisitStates = (
    propsToMap?: Array<$Keys<VisitStatesType>>,
    alias?: {
        [$Keys<VisitStatesType>]: string
    }
): ((Component: React.ComponentType<*>) => React.ComponentType<*>) => (
    Component: React.ComponentType<*>
) => {
    return props => {
        const [state, dispatch] = React.useContext(VisitStatesContext);
        const propsToInject: $Shape<VisitStatesType> =
            propsToMap?.reduce((acc, prop: $Keys<VisitStatesType>) => {
                const propName = alias && alias[prop] ? alias[prop] : prop;
                return {
                    ...acc,
                    [propName]: state[prop]
                };
            }, {}) || {};
        return (
            <Component
                {...props}
                {...propsToInject}
                visitStatesDispatch={dispatch}
            />
        );
    };
};
